import React from "react";
import { ArrowUpRight, ImageIcon } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { Reveal, Kicker } from "../motion";
import grainHarvestImg from "../../assets/news/grain-harvest-forecast-2026-new.webp";
import agricultureGrowthImg from "../../assets/news/agriculture-growth-fruit-2026.webp";
import apkExportImg from "../../assets/news/apk-export-growth-2026-new.webp";
import financialReportImg from "../../assets/news/financial-report-2024-new.webp";
import floodRecoveryImg from "../../assets/news/flood-recovery-2024-new.webp";
import smeGrowthImg from "../../assets/news/sme-growth-2026-new.webp";

// Indexed to match `news.items` in i18n/content.js: harvest forecast,
// agri-growth, APK exports, financial report, flood recovery.
const NEWS_IMAGES = [grainHarvestImg, agricultureGrowthImg, apkExportImg, financialReportImg, floodRecoveryImg, smeGrowthImg];

export function News({ hideIntro = false }) {
  const { c } = useApp();
  const n = c.news;

  return (
    <section id="news" data-testid="news-section" className="relative bg-[#f5f7fa] py-24 md:py-32">
      <div className="mx-auto max-w-[1240px] px-5 md:px-8">
        {!hideIntro && <Reveal className="max-w-2xl">
          <Kicker>{n.kicker}</Kicker>
          <h2 className="mt-5 font-display text-4xl font-bold tracking-tight text-[#0a1a2e] sm:text-5xl lg:text-6xl">
            {n.title}
          </h2>
          <p className="mt-4 text-base text-[#3e516a] md:text-lg">{n.subtitle}</p>
        </Reveal>}

        <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {n.items.map((item, i) => (
            <Reveal
              key={item.title}
              delay={i * 0.08}
              as="article"
              className="card-lift group flex flex-col overflow-hidden rounded-3xl border border-black/5 bg-white"
            >
              <div className="relative aspect-[16/10] overflow-hidden">
                {NEWS_IMAGES[i] ? (
                  <img
                    src={NEWS_IMAGES[i]}
                    alt=""
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center gap-2 border-b border-dashed border-[#002355]/15 bg-[#002355]/[0.03] text-xs font-medium uppercase tracking-wide text-[#002355]/30">
                    <ImageIcon size={16} strokeWidth={1.7} />
                    Фото
                  </div>
                )}
                <span className="absolute left-4 top-4 rounded-full bg-[#e8a93d] px-3 py-1 text-xs font-bold text-[#002355]">
                  {item.cat}
                </span>
              </div>
              <div className="flex flex-1 flex-col p-7">
                <span className="text-xs font-semibold uppercase tracking-wide text-[#3e516a]/70">{item.date}</span>
                <h3 className="mt-3 font-display text-xl font-bold tracking-tight text-[#0a1a2e]">
                  {item.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-[#3e516a]">{item.excerpt}</p>
                <a
                  href={item.href}
                  target={item.external ? "_blank" : undefined}
                  rel={item.external ? "noopener noreferrer" : undefined}
                  data-testid={`news-more-${i}`}
                  className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-[#002355] transition-colors duration-300 hover:text-[#c99311]"
                >
                  {item.external ? c.cta.source : c.cta.more}
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
